import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import VideoCard from "@/components/feature/VideoCard";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import { videos } from "@/mocks/videos";
import { categories } from "@/mocks/categories";
import { useSeo } from "@/hooks/useSeo";
import { durationToSeconds } from "@/utils/format";
import FilterBar, { type Filters } from "./components/FilterBar";

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { type = "" } = useParams();
  const category = categories.find((c) => c.slug === type);
  const [filters, setFilters] = useState<Filters>({ sort: "latest", duration: "all", author: "all" });
  const [page, setPage] = useState(1);

  useSeo({
    title: category ? `${category.name} - 呱呱视频社区` : "分类 - 呱呱视频社区",
    description: category
      ? `在呱呱视频社区浏览${category.name}视频内容，涵盖丰富多样的精彩视频。`
      : "在呱呱视频社区浏览各类视频内容，涵盖丰富多样的精彩视频。",
    keywords: category ? `${category.name},呱呱视频社区,视频分类,短视频` : "呱呱视频社区,视频分类",
  });

  const list = useMemo(() => {
    if (!category) return [];
    let result = videos.filter((v) => v.category === category.slug);
    if (filters.duration === "short") result = result.filter((v) => durationToSeconds(v.duration) < 180);
    if (filters.duration === "long") result = result.filter((v) => durationToSeconds(v.duration) >= 180);
    if (filters.author !== "all") result = result.filter((v) => v.authorType === filters.author);
    result.sort((a, b) => (filters.sort === "hot" ? b.views - a.views : a.daysAgo - b.daysAgo));
    return result;
  }, [category, filters]);

  const totalPages = Math.max(1, Math.ceil(list.length / PAGE_SIZE));
  const current = Math.min(page, totalPages);
  const pageItems = list.slice((current - 1) * PAGE_SIZE, current * PAGE_SIZE);

  const updateFilters = (next: Filters) => {
    setFilters(next);
    setPage(1);
  };

  if (!category) {
    return (
      <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-12">
        <Breadcrumb items={[{ label: "分类未找到" }]} />
        <div className="mt-6">
          <EmptyState
            title="没有找到这个分类"
            description="你访问的分类不存在或已下线，回到首页看看其他精彩内容吧。"
            actionLabel="返回首页"
            onAction={() => window.REACT_APP_NAVIGATE?.("/")}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-8">
      <Breadcrumb items={[{ label: category.name }]} />

      <header className="mt-5 mb-6 p-5 md:p-6 rounded-lg bg-background-100 border border-background-200">
        <div className="flex items-center gap-3">
          <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-500 text-background-50 text-2xl">
            <i className={category.icon} aria-hidden="true"></i>
          </span>
          <div>
            <h1 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950">
              {category.name}
            </h1>
            <p className="mt-1 text-sm text-foreground-600">{category.description}</p>
          </div>
        </div>
      </header>

      <div className="mb-6">
        <FilterBar filters={filters} onChange={updateFilters} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-foreground-500">
          共找到 <span className="text-primary-600 font-semibold">{list.length}</span> 个视频
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          {categories
            .filter((c) => c.slug !== category.slug)
            .map((c) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="h-8 px-3 inline-flex items-center gap-1 rounded-full bg-secondary-100 text-secondary-900 text-xs hover:bg-secondary-200 transition-colors"
              >
                <i className={c.icon} aria-hidden="true"></i>
                {c.name}
              </Link>
            ))}
        </div>
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
            {pageItems.map((v) => (
              <VideoCard
                key={v.id}
                id={v.id}
                title={v.title}
                cover={v.cover}
                author={v.author}
                category={v.category}
                views={v.views}
                duration={v.duration}
                publishDate={v.publishDate}
                authorType={v.authorType}
              />
            ))}
          </div>
          <div className="mt-8">
            <Pagination page={current} totalPages={totalPages} onChange={setPage} />
          </div>
        </>
      ) : (
        <EmptyState
          title="没有符合条件的视频"
          description="当前筛选条件下暂时没有匹配内容，试试调整或重置筛选条件。"
          actionLabel="重置筛选"
          onAction={() => updateFilters({ sort: "latest", duration: "all", author: "all" })}
        />
      )}
    </div>
  );
}